import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Vertex } from "../points";
import { Page } from "./Page";
import Path from "../Path";
import { Length, convertLength } from "../length";
import "./pdf.scss";
import { PageDimensions, PatternSectionPages } from "./PatternSectionPages";
import { useAppSelector } from "../../app/hooks";
import { selectProject } from "../patternSlice";

interface PdfViewProps {
  scale: number;
  mirror: boolean;
  vertices: Vertex[];
  onClick: Function;
  patternName: string;
}

export function PdfView({
  scale,
  mirror,
  vertices,
  onClick,
  patternName,
}: PdfViewProps) {
  const { t } = useTranslation();
  const pathsRef = useRef<SVGSVGElement>(null);
  const project = useAppSelector(selectProject);
  const [pathDimensions, setPathDimensions] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (pathsRef.current) {
      const bbox = pathsRef.current.getBBox();
      setPathDimensions({
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height,
      });
    }
  }, [pathsRef, setPathDimensions]);

  const viewBoxWidth = 1000;
  const viewBoxHeight = 600;
  const viewBox = `0 0 ${viewBoxWidth} ${viewBoxHeight}`;

  //TODO: read this from the user/default settings
  const _pageDimensions: PageDimensions = {
    width: { value: 8.5, unit: "in" },
    height: { value: 11, unit: "in" },
  };
  const _printMargin: Length = {
    value: 10,
    unit: "mm",
  };
  ///

  const pageDimensions = {
    width: convertLength(_pageDimensions.width, "cm"),
    height: convertLength(_pageDimensions.height, "cm"),
  };
  const printMargin = convertLength(_printMargin, "cm");

  const sectionsMargin = printMargin.value / scale;

  const workingWidth = pageDimensions.width.value - printMargin.value * 2;
  const workingHeight = pageDimensions.height.value - printMargin.value * 2;

  const patternWidth = pathDimensions.width;
  const patternHeight = pathDimensions.height;

  const sectionsVertical = (patternHeight * scale) / workingHeight;
  const pagesVertical = Math.ceil(sectionsVertical);
  const sectionsHorizontal = (patternWidth * scale) / workingWidth;
  const pagesHorizontal = Math.ceil(sectionsHorizontal);
  const patternSectionWidth = pageDimensions.width.value / scale;
  const patternSectionHeight = pageDimensions.height.value / scale;

  const zoom = 0.3;
  const zoomFitHorizontal = workingWidth / (pagesHorizontal * workingWidth);
  const marginBottom = 2; //leave the page number free
  const zoomFitVertical =
    (workingHeight - marginBottom) / 2 / (pagesVertical * workingHeight);
  const miniatureZoom = Math.min(zoomFitHorizontal, zoomFitVertical);

  const morePaths = project.patterns.map((pattern, i) => {
    return <Path key={i} vertices={pattern.vertices} mirror={pattern.mirror} />;
  });

  return (
    <div id="pdf" className="pdf-printing" onClick={() => onClick()}>
      <svg
        id="svg"
        viewBox={viewBox}
        style={{
          border: "thin solid red",
          boxSizing: "border-box",
          position: "absolute",
          visibility: "hidden",
        }}
      >
        <g className="pattern-paths" ref={pathsRef}>
          {morePaths}
        </g>
      </svg>

      <div
        id="pages-wrapper"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${pagesHorizontal}, 1fr)`,
          gap: 0,
          width: "fit-content",
        }}
      >
        <PatternSectionPages
          patternX={pathDimensions.x}
          patternY={pathDimensions.y}
          patternHeight={patternHeight}
          patternWidth={patternWidth}
          sectionsVertical={sectionsVertical}
          sectionsHorizontal={sectionsHorizontal}
          pagesVertical={pagesVertical}
          pagesHorizontal={pagesHorizontal}
          margin={sectionsMargin}
          pageMargin={printMargin}
          pageDimensions={pageDimensions}
          width={patternSectionWidth}
          height={patternSectionHeight}
          scale={scale}
          patterns={project.patterns}
          zoom={zoom}
        />
      </div>

      <Page className="instructions-page" zoom={zoom}>
        <div
          style={{
            fontSize: 50,
            position: "absolute",
            bottom: "1cm",
            right: "2cm",
          }}
        >
          0
        </div>
        <div
          style={{
            fontSize: 25,
            textAlign: "center",
            paddingTop: "0.5cm",
          }}
        >
          <h1>{patternName}</h1>
          <p
            style={{
              whiteSpace: "pre-wrap",
            }}
          >
            {t("pdf print instrucctions")}
          </p>

          <div
            className="measurements"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <div
              style={{
                border: "1mm solid black",
                boxSizing: "border-box",
                width: "5cm",
                height: "5cm",
                lineHeight: "5cm",
                left: "3cm",
                fontSize: "24pt",
              }}
            >
              |-- 5cm --|
            </div>

            <div
              style={{
                border: "1mm solid black",
                boxSizing: "border-box",
                width: "2in",
                height: "2in",
                lineHeight: "2in",
                right: "3cm",
                fontSize: "24pt",
              }}
            >
              |-- 2in --|
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "1cm",
              left: "1cm",
              fontSize: "14pt",
            }}
          >
            Made with parasvg
          </div>

          <div
            id="all-print"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${pagesHorizontal}, 1fr)`,
              gap: 0,
              width: "fit-content",
              marginLeft: "auto",
              marginRight: "auto",
              marginTop: "1cm",
            }}
          >
            <PatternSectionPages
              patternX={pathDimensions.x}
              patternY={pathDimensions.y}
              patternHeight={patternHeight}
              patternWidth={patternWidth}
              sectionsVertical={sectionsVertical}
              sectionsHorizontal={sectionsHorizontal}
              pagesVertical={pagesVertical}
              pagesHorizontal={pagesHorizontal}
              pageDimensions={pageDimensions}
              pageMargin={printMargin}
              margin={sectionsMargin}
              width={patternSectionWidth}
              height={patternSectionHeight}
              scale={scale}
              patterns={project.patterns}
              zoom={miniatureZoom}
              printMiniature={true}
            />
          </div>
        </div>
      </Page>
    </div>
  );
}
